import { useAppUser, useEnvProvider, useModulePermission } from '@bechtel/client-common';
import { Map, Marker, useProjectCommodityTypesById } from '@bechtel/client-weld-common';
import { Box, Spinner, SpinnerSize } from '@bechtel/gds';
import {
  CommodityComplexFilterInput,
  CommodityFilterByTerm,
  CommodityStatusCode,
  FilterOperator,
  MapMarker,
  MarkerType,
  PersonaType,
  Project,
} from '@bechtel/types';
import { find, findIndex, forEach, get, map, some, toLower } from 'lodash';
import { FC, useEffect, useState } from 'react';
import { CommodityInfo } from '~/components/molecules';
import { RodRoomInfo } from '~/components/molecules/rod-room-info/internals';
import {
  useFweApprovedMapMarkersQuery,
  useFweFitupMapMarkersQuery,
  useFweMapMarkersQuery,
  useMapMarkersQuery,
} from '~/hooks';
// @ts-ignore: tsc declarations not recognising types/index.d.ts
import PipeIcon from '@bechtel/client-assets/dist/images/pipe-icon.svg';
// @ts-ignore: tsc declarations not recognising types/index.d.ts
import SteelIcon from '@bechtel/client-assets/dist/images/steel-icon.svg';
// @ts-ignore: tsc declarations not recognising types/index.d.ts
import DataVaultIcon from '@bechtel/client-assets/dist/images/data-vault-icon.svg';
// @ts-ignore: tsc declarations not recognising types/index.d.ts
import RodRoomIcon from '@bechtel/client-assets/dist/images/rod-room-icon.svg';
import { ConnectionType, MapMarkersProps } from './types';

export const MapMarkers: FC<MapMarkersProps> = ({ crewMember, weldFilter }) => {
  const { user } = useAppUser();
  const { envConfig } = useEnvProvider();
  const { canViewRodRoomModule } = useModulePermission();
  const [markers, setMarkers] = useState<Marker[]>([]);

  const [fweMarkers, setFWEMarkers] = useState<Marker[]>([]);
  const [fweFitupMarkers, setFWEFitupMarkers] = useState<Marker[]>([]);
  const [fweApprovedMarkers, setFWEApprovedMarkers] = useState<Marker[]>([]);
  const [getProject, { called: projectCalled, data: projectData }] = useProjectCommodityTypesById();
  const projectDetailArr: Project[] = get(projectData, 'projectsByIds', []);
  const projectDetail = projectDetailArr.length > 0 ? projectDetailArr[0] : null;
  const weldTypeId = find(projectDetail?.projectConfiguration.projectCommodityTypes, (item) => {
    return toLower(item.name) === 'weld';
  })?.id;
  const currentProjectWeek = find(user?.project?.projectConfiguration?.projectWeeks, (projectWeek) =>
    projectWeek?.isCurrent ? true : false
  );
  const markerAllTypes = canViewRodRoomModule ? [MarkerType.PIPING, MarkerType.ROD_ROOM] : [MarkerType.PIPING];
  const [selectedMarkerTypes, setSelectedMarkerTypes] = useState<MarkerType[]>([
    MarkerType.PIPING,
    MarkerType.ROD_ROOM,
  ]);
  const projectLocationObj = user?.project?.projectConfiguration?.projectLocation;
  const maxLatitude = projectLocationObj?.maxLatitude;
  const maxLongitude = projectLocationObj?.maxLongitude;
  const minLatitude = projectLocationObj?.minLatitude;
  const minLongitude = projectLocationObj?.minLongitude;

  const getWeldFilters = (markerFilter) => {
    const complexFilterInput: CommodityComplexFilterInput = {
      and: [
        {
          term: CommodityFilterByTerm.COMMODITY_TYPE_ID,
          value: weldTypeId,
          operator: FilterOperator.EQUALS,
        },
      ],
      or: [],
    };

    if (
      findIndex(markerFilter, (selectedItem) => {
        return MarkerType.PIPING === selectedItem;
      }) !== -1
    ) {
      complexFilterInput.and?.push({
        term: CommodityFilterByTerm.CONNECTION_TYPE,
        values: [ConnectionType.PIPE],
        operator: FilterOperator.IN,
      });
    }
    if (crewMember?.persona && crewMember.persona === PersonaType.FIELD_WELD_ENGINEER) {
      complexFilterInput.and?.push(
        {
          term: CommodityFilterByTerm.PERSONA_TYPE_ID,
          value: crewMember?.persona,
          operator: FilterOperator.EQUALS,
        },
        {
          term: CommodityFilterByTerm.APPLY_ASSOCIATED_CREW_MEMBERS,
          value: 'true',
          operator: FilterOperator.EQUALS,
        },
        {
          term: CommodityFilterByTerm.PRIMARY_CREW_MEMBER_ID,
          values: weldFilter?.searchByFWE?.length > 0 ? [crewMember?.id, ...weldFilter.searchByFWE] : [crewMember?.id],
          operator: FilterOperator.IN,
        }
      );
    } else {
      complexFilterInput.and?.push({
        term: CommodityFilterByTerm.PRIMARY_CREW_MEMBER_ID,
        value: crewMember?.id,
        operator: FilterOperator.EQUALS,
      });
    }

    if (weldFilter?.searchByWPS?.length > 0) {
      complexFilterInput.and?.push({
        term: CommodityFilterByTerm.WELD_PROCEDURE_ID,
        values: weldFilter?.searchByWPS,
        operator: FilterOperator.IN,
      });
    }

    if (weldFilter?.searchByForeman?.length > 0) {
      complexFilterInput.and?.push({
        term: CommodityFilterByTerm.CREW_MEMBER_ID,
        values: [...weldFilter.searchByForeman],
        operator: FilterOperator.IN,
      });
    }

    if (weldFilter?.searchByDrawing?.length > 0) {
      complexFilterInput.and?.push({
        term: CommodityFilterByTerm.DRAWING_NUMBER,
        values: weldFilter?.searchByDrawing,
        operator: FilterOperator.IN,
      });
    }
    if (weldFilter?.drawerValues?.weldTypes?.length > 0)
      complexFilterInput.and?.push({
        term: CommodityFilterByTerm.COMMODITY_GROUP_TYPE_ID,
        values: weldFilter?.drawerValues?.weldTypes,
        operator: FilterOperator.IN,
      });
    if (weldFilter?.drawerValues?.weldSizeList?.length === 1) {
      const isSmallBore = some(weldFilter?.drawerValues?.weldSizeList, (item) => {
        return item === CommodityFilterByTerm.SMALL_BORE;
      });
      complexFilterInput.and?.push({
        term: CommodityFilterByTerm.NOMINAL_DIAMETER,
        value: '2.5',
        operator: isSmallBore ? FilterOperator.LESS_THAN : FilterOperator.GREATER_THAN,
      });
    }
    if (weldFilter?.drawerValues?.isPriority) {
      complexFilterInput.and?.push({
        term: CommodityFilterByTerm.PRIORITY_WELDS,
        value: 'true',
        operator: FilterOperator.EQUALS,
      });
    }
    if (weldFilter?.searchKeyword.length > 0)
      forEach(weldFilter?.drawerValues.searchByList, (item: CommodityFilterByTerm) => {
        complexFilterInput.or?.push({
          term: item,
          value: weldFilter?.searchKeyword,
          operator: item == CommodityFilterByTerm.CREW_MEMBER_NAME ? FilterOperator.FUZZY : FilterOperator.CONTAINS,
        });
      });

    return complexFilterInput;
  };
  const getFormattedMarkerList = (mapMarkersData) => {
    const commoditiesMapMarkers: MapMarker[] = get(mapMarkersData?.mapMarkers, 'commodities', []);
    const rodRoomsMapMarkers: MapMarker[] = get(mapMarkersData?.mapMarkers, 'rodRooms', []);
    const dataVaultsMapMarkers: MapMarker[] = get(mapMarkersData?.mapMarkers, 'dataVaults', []);
    const formatedCommoditiesMapMarkers =
      commoditiesMapMarkers && commoditiesMapMarkers.length > 0
        ? map(commoditiesMapMarkers, (commoditiesMapMarker) => {
            return {
              id: commoditiesMapMarker?.id,
              displayIdentifier: commoditiesMapMarker?.displayIdentifier,
              markerType: commoditiesMapMarker?.type,
              icon: commoditiesMapMarker.type == MarkerType.STEEL ? SteelIcon : PipeIcon,
              position: {
                lat: commoditiesMapMarker?.geoLocation?.latitude,
                lng: commoditiesMapMarker?.geoLocation?.longitude,
              },
              locationDescription: commoditiesMapMarker?.locationDescription
                ? commoditiesMapMarker?.locationDescription
                : '',
            };
          })
        : [];
    const formatedRodRoomsMapMarkers =
      rodRoomsMapMarkers && rodRoomsMapMarkers.length > 0
        ? map(rodRoomsMapMarkers, (rodRoomsMapMarker) => {
            return {
              id: rodRoomsMapMarker?.id,
              displayIdentifier: rodRoomsMapMarker?.displayIdentifier,
              markerType: rodRoomsMapMarker?.type,
              icon: RodRoomIcon,
              position: {
                lat: rodRoomsMapMarker?.geoLocation?.latitude,
                lng: rodRoomsMapMarker?.geoLocation?.longitude,
              },
              locationDescription: rodRoomsMapMarker?.locationDescription ? rodRoomsMapMarker?.locationDescription : '',
            };
          })
        : [];
    const formatedDataVaultsMapMarkers =
      dataVaultsMapMarkers && dataVaultsMapMarkers.length > 0
        ? map(dataVaultsMapMarkers, (dataVaultsMapMarker) => {
            return {
              id: dataVaultsMapMarker?.id,
              displayIdentifier: dataVaultsMapMarker?.displayIdentifier,
              markerType: dataVaultsMapMarker?.type,
              icon: DataVaultIcon,
              position: {
                lat: dataVaultsMapMarker?.geoLocation?.latitude,
                lng: dataVaultsMapMarker?.geoLocation?.longitude,
              },
              locationDescription: dataVaultsMapMarker?.locationDescription
                ? dataVaultsMapMarker?.locationDescription
                : '',
            };
          })
        : [];
    return [...formatedCommoditiesMapMarkers, ...formatedRodRoomsMapMarkers, ...formatedDataVaultsMapMarkers];
  };

  const fweMapMarkersCompleted = (data) => {
    setFWEMarkers(getFormattedMarkerList(data));
  };
  const fweFitupMapMarkersCompleted = (data) => {
    setFWEFitupMarkers(getFormattedMarkerList(data));
  };
  const fweApprovedMapMarkersCompleted = (data) => {
    setFWEApprovedMarkers(getFormattedMarkerList(data));
  };
  const mapMarkersCompleted = (data) => {
    setMarkers(getFormattedMarkerList(data));
  };

  const [getFWEFitupMapMarkers, { loading: fweFitupMapMarkersLoading }] = useFweFitupMapMarkersQuery((data) => {
    fweFitupMapMarkersCompleted(data);
  });
  const [getFWEApprovedMapMarkers, { loading: fweApprovedMapMarkersLoading }] = useFweApprovedMapMarkersQuery(
    (data) => {
      fweApprovedMapMarkersCompleted(data);
    }
  );
  const [getFWEMapMarkers, { loading: fweMapMarkersLoading }] = useFweMapMarkersQuery((data) => {
    fweMapMarkersCompleted(data);
  });
  const [getMapMarkers, { loading: mapMarkersLoading }] = useMapMarkersQuery((data) => {
    mapMarkersCompleted(data);
  });

  useEffect(() => {
    if (!user || !user.project || projectCalled) return;
    getProject({
      variables: {
        ids: [user?.project?.id],
      },
    });
  }, [user]);

  const getMarkerData = (markerTypes) => {
    if (crewMember?.persona && crewMember.persona === PersonaType.FIELD_WELD_ENGINEER) {
      const fweFitupCommodityComplexFilterInput = getWeldFilters(markerTypes);
      fweFitupCommodityComplexFilterInput.and?.push({
        term: CommodityFilterByTerm.COMMODITY_STATUS_ID,
        values: [CommodityStatusCode.FITUP_INSPECTION],
        operator: FilterOperator.IN,
      });
      getFWEFitupMapMarkers({
        variables: {
          types: markerTypes,
          commodityComplexFilter: fweFitupCommodityComplexFilterInput,
        },
      });

      //FWE APPROVED CALL
      const fweApprovedCommodityComplexFilterInput = getWeldFilters(markerTypes);
      fweApprovedCommodityComplexFilterInput.and?.push(
        {
          term: CommodityFilterByTerm.COMMODITY_STATUS_ID,
          values: [CommodityStatusCode.VT_APPROVED],
          operator: FilterOperator.IN,
        },

        {
          term: CommodityFilterByTerm.START_DATE,
          value: currentProjectWeek?.startDate,
          operator: FilterOperator.GREATER_THAN,
        },
        {
          term: CommodityFilterByTerm.END_DATE,
          value: currentProjectWeek?.endDate,
          operator: FilterOperator.LESS_THAN,
        },
        {
          term: CommodityFilterByTerm.ASSIGNED_CREW_MEMBER,
          value: 'true',
          operator: FilterOperator.EQUALS,
        }
      );

      getFWEApprovedMapMarkers({
        variables: {
          types: markerTypes,
          commodityComplexFilter: fweApprovedCommodityComplexFilterInput,
        },
      });

      //FWE  CALL
      const fweCommodityComplexFilterInput = getWeldFilters(markerTypes);
      fweCommodityComplexFilterInput.and?.push(
        {
          term: CommodityFilterByTerm.COMMODITY_STATUS_ID,
          values: [CommodityStatusCode.CLAIMED, CommodityStatusCode.READY_TO_WELD, CommodityStatusCode.VT_SENT_BACK],
          operator: FilterOperator.IN,
        },
        {
          term: CommodityFilterByTerm.ASSIGNED_CREW_MEMBER,
          value: 'true',
          operator: FilterOperator.EQUALS,
        }
      );
      getFWEMapMarkers({
        variables: {
          types: markerTypes,
          commodityComplexFilter: fweCommodityComplexFilterInput,
        },
      });
    } else {
      const commodityComplexFilterInput = getWeldFilters(markerTypes);
      commodityComplexFilterInput.and?.push(
        {
          term: CommodityFilterByTerm.COMMODITY_STATUS_ID,
          values: [
            CommodityStatusCode.CLAIMED,
            CommodityStatusCode.READY_TO_WELD,
            CommodityStatusCode.VT_SENT_BACK,
            CommodityStatusCode.FITUP_INSPECTION,
          ],
          operator: FilterOperator.IN,
        },
        {
          term: CommodityFilterByTerm.ASSIGNED_CREW_MEMBER,
          value: 'true',
          operator: FilterOperator.EQUALS,
        }
      );
      getMapMarkers({
        variables: {
          types: markerTypes,
          commodityComplexFilter: commodityComplexFilterInput,
        },
      });
    }
  };
  const onFilterChange = (markerTypes: MarkerType[]) => {
    setSelectedMarkerTypes(markerTypes);
  };

  useEffect(() => {
    if (
      !weldTypeId ||
      crewMember?.persona === PersonaType.SUPER_ADMIN ||
      (crewMember?.persona === PersonaType.FIELD_WELD_ENGINEER && !weldFilter?.searchByForeman?.length)
    )
      return;

    getMarkerData(selectedMarkerTypes);
  }, [weldTypeId, crewMember, weldFilter, selectedMarkerTypes]);

  const allMarkers = [...fweFitupMarkers, ...fweApprovedMarkers, ...fweMarkers, ...markers];

  const mapCenter: google.maps.LatLngLiteral =
    maxLatitude && maxLongitude && minLatitude && minLongitude
      ? {
          lat: (minLatitude + maxLatitude) / 2,
          lng: (minLongitude + maxLongitude) / 2,
        }
      : {
          lat: Number(allMarkers.length > 0 && allMarkers[0]?.position?.lat),
          lng: Number(allMarkers.length > 0 && allMarkers[0]?.position?.lng),
        };

  const renderInfoWindowContent = (item: Marker) => {
    switch (item?.markerType) {
      case MarkerType.ROD_ROOM:
        return (
          <RodRoomInfo
            rodRoomId={item?.id}
            rodRoomName={item?.displayIdentifier}
            locationDescription={item?.locationDescription}
          />
        );
      case MarkerType.PIPING:
        return (
          <CommodityInfo
            commodityId={item.id}
            commodityTag={item?.displayIdentifier}
            locationDescription={item?.locationDescription}
          />
        );
      default:
        return undefined;
    }
  };
  const markerList: Marker[] =
    allMarkers && allMarkers?.length > 0
      ? allMarkers.map((item: Marker) => {
          const marker = item;
          marker.infoWindowContent = renderInfoWindowContent(item);
          return marker;
        })
      : [];

  const loading =
    fweFitupMapMarkersLoading || fweApprovedMapMarkersLoading || fweMapMarkersLoading || mapMarkersLoading;
  return (
    <>
      {loading && (
        <Box
          css="height: 100%;"
          data-testid="loading"
          justifyContent={{ sm: 'center' }}
          alignItems={{ sm: 'center' }}
          flexGrow={{ sm: 1 }}
        >
          <Spinner size={SpinnerSize.LARGE} />
        </Box>
      )}
      {!loading && (
        <Map
          apiKey={envConfig?.mapApiKey}
          zoom={11}
          // TODO: need lat/lng to center the map at some position
          center={mapCenter}
          markers={markerList}
          // setting the value using enum from google.maps.MapTypeId throwing google undefined error.needs debugging. With string, it's working fine.
          mapTypeId="satellite"
          hasFilter={true}
          onFilterChange={onFilterChange}
          selectedMarkerTypes={selectedMarkerTypes}
          setSelectedMarkerTypes={setSelectedMarkerTypes}
          markerAllTypes={markerAllTypes}
        />
      )}
    </>
  );
};
